/* cspell:ignore nics */
import i18n from '@/i18n';
import type { NicConfigurationItem } from '@/types/nic-configuration';

import type { VolumeItem } from '../../../capabilities/container-storage';
import { buildPricedNicResourceConfigs, getPricedNics } from '../../../components/nicPricing/utils';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import { buildMiDisplayDescriptors, buildMiResourceConfigs } from '../../ModelInstance/headless/projections';
import type { MiSpec } from '../../ModelInstance/headless/schema';
import { miFromForm } from '../../ModelInstance/headless/spec';
import {
  appendPerNodeSystemVolumeDisplayRows,
  appendSystemVolumeResourceConfigs,
  parseMaasNodeGroups,
  toResourceConfig,
} from '../../_shared/workload-agg/displayHelpers';
import { buildTrainingSharedVolumeResourceConfigs } from '../resourceConfigs';
import type { TcApiPayload, TcPluginSpec } from './schema';
import { buildTrainingClusterSpec, getTrainingClusterImage } from './trainingClusterSpec';

const t = i18n.t.bind(i18n);
const TRAINING_SYSTEM_VOLUME_RESOURCE: ResourceType = {
  requirement: '',
  code: 'SystemVolume',
  description: 'System Volume',
  unit: 'Gi',
};
/** 训练集群借用模型实例投影时的模块边界；未复用模块必须显式关闭，避免下单空模块语义将其重新启用。 */
const MI_MODULE_KEYS = [
  'modelStorage',
  'systemConfig',
  'healthCheck',
  'service',
  'envVars',
  'storage',
  'nodeGroups',
  'nic',
  'initCommand',
  'taskConfig',
];

function isModuleEnabled(spec: TcPluginSpec, ctx: PipelineContext, moduleKey: string): boolean {
  const modules = ctx.sku?.enabledModules ?? (spec.skuResourceModules as Record<string, boolean> | undefined);
  if (modules && Object.keys(modules).length === 0) return true;
  if (ctx.sku?.skuType === 'custom') return modules?.[moduleKey] === true;
  if (!modules) return true;
  return modules[moduleKey] !== false;
}

function buildModelInstanceEnabledModules(spec: TcPluginSpec, ctx: PipelineContext) {
  return Object.fromEntries(
    MI_MODULE_KEYS.map((key) => {
      if (key === 'healthCheck') {
        // 探活嵌入 nodeGroups 高级配置，随 nodeGroups 模块保留 livenessProbe
        return [key, isModuleEnabled(spec, ctx, 'nodeGroups')];
      }
      return [key, ['envVars', 'nodeGroups', 'initCommand'].includes(key) && isModuleEnabled(spec, ctx, key)];
    }),
  );
}

function buildModelInstanceContext(spec: TcPluginSpec, ctx: PipelineContext): PipelineContext {
  const enabledModules = buildModelInstanceEnabledModules(spec, ctx);
  return {
    ...ctx,
    sku: {
      ...ctx.sku,
      enabledModules,
    },
  };
}

/**
 * 静态调用 MI headless pure（禁止经 registry 运行时取 pipeline，避免注册顺序耦合）。
 * 跨插件单向依赖属跨插件耦合，待上收；后续有痛再上收至 `_shared/workload-agg`（展示胶水）。
 */
function buildModelInstanceSpec(spec: TcPluginSpec, ctx: PipelineContext): MiSpec {
  const enabledModules = buildModelInstanceEnabledModules(spec, ctx);
  return miFromForm({ ...spec, skuResourceModules: enabledModules }, buildModelInstanceContext(spec, ctx), {
    layout: 'flatGroups',
  });
}

function getGroupEmbeddedNics(group: unknown): NicConfigurationItem[] {
  const resource = (group as { resource?: { nics?: NicConfigurationItem[]; network?: { nics?: NicConfigurationItem[] } } } | undefined)
    ?.resource;
  const nics = resource?.nics ?? resource?.network?.nics;
  return Array.isArray(nics) ? nics : [];
}

function getTrainingNics(modelSpec: MiSpec, spec: TcPluginSpec, ctx: PipelineContext): NicConfigurationItem[] | undefined {
  if (!isModuleEnabled(spec, ctx, 'nodeGroups')) return undefined;
  const nics = [modelSpec.headGroupSpec, ...(modelSpec.workerGroupSpecs || [])].flatMap(getGroupEmbeddedNics);
  if (!nics.length) return undefined;
  const network = spec.network as { nicPricing?: unknown } | undefined;
  return getPricedNics(nics, network?.nicPricing);
}

function getTrainingSharedVolumes(spec: TcPluginSpec, ctx: PipelineContext): VolumeItem[] | undefined {
  if (!isModuleEnabled(spec, ctx, 'storage')) return undefined;
  return Array.isArray(spec.trainingSharedVolumes) ? (spec.trainingSharedVolumes as VolumeItem[]) : undefined;
}

/** 下单计费中的网卡需覆盖 Head 与全部 Worker 副本。 */
function getTrainingClusterNodeReplicaCount(spec: MiSpec): number {
  return [spec.headGroupSpec, ...(spec.workerGroupSpecs || [])].reduce((total, group) => {
    if (!group) return total;
    const replicas = Number(group.replicas);
    return total + (Number.isFinite(replicas) && replicas > 0 ? replicas : 1);
  }, 0);
}

/**
 * 下单（含实时询价）NIC 按 Head/Worker 总副本放大。
 * 优先 resourceEditMode / resourceQuantityPurpose；空 enabledModules 仅作旧调用兼容。
 */
function isTrainingClusterOrderContext(ctx: PipelineContext): boolean {
  if (ctx.resourceQuantityPurpose === 'order') return true;
  const mode = ctx.resourceEditMode;
  if (mode === 'order-fixed' || mode === 'order-custom' || mode === 'order-post-paid') return true;
  return ctx.sku?.enabledModules != null && Object.keys(ctx.sku.enabledModules).length === 0;
}

/** 训练集群插件规格转创建接口请求体。 */
export function serializeTrainingClusterApiBody(spec: TcPluginSpec, ctx: PipelineContext): TcApiPayload {
  const modelSpec = buildModelInstanceSpec(spec, ctx);
  return buildTrainingClusterSpec(
    { ...modelSpec, datacenter: ctx.cluster ?? ctx.overlay?.datacenter },
    getTrainingSharedVolumes(spec, ctx),
  ) as TcApiPayload;
}

/** 训练集群 SKU/确认页展示描述。 */
export function buildTrainingClusterDisplayDescriptors(spec: TcPluginSpec, ctx: PipelineContext): DisplayDescriptor[] {
  const modelCtx = buildModelInstanceContext(spec, ctx);
  const modelSpec = buildModelInstanceSpec(spec, ctx);
  const nodes = parseMaasNodeGroups(spec);
  const rows = buildMiDisplayDescriptors(modelSpec, modelCtx)
    .filter((item) => ['systemConfig', 'envVars', 'nodeGroups'].includes(item.group))
    .flatMap((item) => {
      if (item.id === 'inference_framework') {
        const trainingImage = getTrainingClusterImage(modelSpec.headGroupSpec, modelSpec.workerGroupSpecs);
        return [{ ...item, label: t('data-center:training.cluster.image'), value: trainingImage || item.value }];
      }
      return item.id.startsWith('system-volume-') ? [] : [item];
    });

  appendPerNodeSystemVolumeDisplayRows(
    spec,
    nodes,
    (item) =>
      rows.push({
        ...item,
        group: 'nodeGroups',
        label: item.label.replace('(master)', '(Head)'),
      }),
    t,
    { masterRoleLabel: 'Head' },
  );

  if (isModuleEnabled(spec, ctx, 'systemConfig')) {
    const runtimeType = String((spec.runtime as { type?: unknown } | undefined)?.type || '').trim();
    if (runtimeType) {
      rows.unshift({
        id: 'training_runtime',
        group: 'systemConfig',
        label: t('data-center:maas.runtime.type'),
        value: runtimeType,
      });
    }
  }

  const nics = getTrainingNics(modelSpec, spec, ctx);
  if (nics?.length) {
    const network = spec.network as { nicPricing?: unknown } | undefined;
    const nicResources = buildPricedNicResourceConfigs(nics, network?.nicPricing);
    const value = nicResources
      .map((item) => `${item.device || item.deviceType || 'NIC'} * ${item.requirement}${item.unit ? ` ${item.unit}` : ''}`)
      .join('、');
    const vendors = [...new Set(nicResources.map((item) => item.vendor).filter(Boolean))] as string[];
    rows.push({
      // 沿用历史 ID，编辑 SKU 时可将旧“网段列表”原位更新为“网卡”。
      id: 'training_network_segments',
      group: 'nodeGroups',
      label: t('data-center:network.card'),
      value,
      ...(vendors.length ? { tag: vendors.join('、') } : {}),
    });
  }

  const volumes = getTrainingSharedVolumes(spec, ctx);
  if (volumes?.length) {
    const totalGi = volumes.reduce((total, item) => total + ('volumeCapacity' in item ? Number(item.volumeCapacity) || 0 : 0), 0);
    rows.push({
      id: 'training-shared-storage-total',
      group: 'storage',
      label: t('common:shared.storage.volume'),
      value: totalGi > 0 ? `${totalGi} Gi` : String(volumes.length),
    });
  }

  if (isModuleEnabled(spec, ctx, 'initCommand')) {
    const initCommand = spec.initCommand as { _commandsText?: unknown; _argsText?: unknown } | undefined;
    const commandText = String(initCommand?._commandsText || '').trim();
    const argsText = String(initCommand?._argsText || '').trim();
    const value = [commandText, argsText].filter(Boolean).join('\n');
    if (value) {
      rows.push({
        id: 'training_init_command',
        group: 'initCommand',
        label: t('data-center:maas.init.command'),
        value,
      });
    }
  }

  return rows;
}

/** 训练集群 SKU 计价资源。 */
export function buildTrainingClusterResourceConfigs(spec: TcPluginSpec, ctx: PipelineContext): ResourceType[] {
  const modelCtx = buildModelInstanceContext(spec, ctx);
  const modelSpec = buildModelInstanceSpec(spec, ctx);
  const modelResources = buildMiResourceConfigs(modelSpec, modelCtx).filter(
    (item) => item.code !== TRAINING_SYSTEM_VOLUME_RESOURCE.code && item.code !== 'NIC',
  );
  appendSystemVolumeResourceConfigs(modelResources, TRAINING_SYSTEM_VOLUME_RESOURCE, spec, parseMaasNodeGroups(spec), toResourceConfig);
  const storageResources = isModuleEnabled(spec, ctx, 'storage')
    ? buildTrainingSharedVolumeResourceConfigs(spec.trainingSharedVolumes, ctx)
    : [];
  const network = spec.network as { nicPricing?: unknown } | undefined;
  const nodeReplicaCount = isTrainingClusterOrderContext(ctx) ? getTrainingClusterNodeReplicaCount(modelSpec) : 1;
  const nicResources = buildPricedNicResourceConfigs(getTrainingNics(modelSpec, spec, ctx), network?.nicPricing).map((resource) => ({
    ...resource,
    requirement: String(Number(resource.requirement) * nodeReplicaCount),
  }));
  return [...modelResources, ...nicResources, ...storageResources];
}

/** 训练集群 SKU 预置数据保持扁平表单结构。 */
export function pickTrainingClusterPresetFields(spec: TcPluginSpec): TcPluginSpec {
  return spec;
}
